import React, { useMemo } from 'react';
import { Box, Card, CardContent, Typography, Tooltip, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid2';
import InfoIcon from '@mui/icons-material/Info';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { useTheme } from '@mui/material/styles';

const MetricCard = ({ title, value, change, tooltip }) => {
  const theme = useTheme();
  const isPositive = change >= 0;
  
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" color="textSecondary">
            {title}
          </Typography>
          <Tooltip title={tooltip} arrow>
            <IconButton size="small" sx={{ ml: 1 }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="h4" component="div" sx={{ mb: 1 }}>
          {value}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isPositive ? (
            <TrendingUpIcon sx={{ color: theme.palette.success.main, mr: 1 }} />
          ) : (
            <TrendingDownIcon sx={{ color: theme.palette.error.main, mr: 1 }} />
          )}
          <Typography
            variant="body2"
            color={isPositive ? 'success.main' : 'error.main'}
          >
            {Math.abs(change)}% from last period
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default function DetailedStats({ data = [] }) {
  const stats = useMemo(() => {
    // Ensure data is valid and split into current and previous periods
    const validData = Array.isArray(data) ? data.filter(item => 
      item && item.timestamp && !isNaN(new Date(item.timestamp).getTime())
    ) : [];

    const sortedData = validData.sort((a, b) => {
      try {
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      } catch {
        return 0;
      }
    });
    
    const midPoint = Math.floor(sortedData.length / 2);
    const currentPeriod = sortedData.slice(0, midPoint || 1); // Ensure at least one item in current period
    const previousPeriod = sortedData.slice(midPoint);

    // Calculate metrics for both periods
    const calculateMetrics = (periodData) => {
      const metrics = {
        avgChargingTime: 0,
        avgTargetSoc: 0,
        completionRate: 0,
        peakUsage: 0
      };
      
      if (periodData.length === 0) return metrics;

      let totalTime = 0;
      let totalSoc = 0;
      let completed = 0;
      const hourCounts = new Array(24).fill(0);

      periodData.forEach(record => {
        if (record) {
          if (record.userSelectedTime) totalTime += record.userSelectedTime;
          if (record.targetSoc) totalSoc += record.targetSoc;
          if (record.completed) completed++;
          if (record.timestamp) {
            const hour = new Date(record.timestamp).getHours();
            hourCounts[hour]++;
          }
        }
      });

      metrics.avgChargingTime = totalTime / periodData.length;
      metrics.avgTargetSoc = totalSoc / periodData.length;
      metrics.completionRate = (completed / periodData.length) * 100;
      metrics.peakUsage = Math.max(...hourCounts);

      return metrics;
    };

    const current = calculateMetrics(currentPeriod);
    const previous = calculateMetrics(previousPeriod);

    // Calculate percentage changes
    const calculateChange = (current, previous) => 
      previous === 0 ? 0 : ((current - previous) / previous) * 100;

    return {
      chargingTime: {
        value: current.avgChargingTime.toFixed(1) + 'h',
        change: calculateChange(current.avgChargingTime, previous.avgChargingTime)
      },
      targetSoc: {
        value: Math.round(current.avgTargetSoc) + '%',
        change: calculateChange(current.avgTargetSoc, previous.avgTargetSoc)
      },
      completionRate: {
        value: Math.round(current.completionRate) + '%',
        change: calculateChange(current.completionRate, previous.completionRate)
      },
      peakUsage: {
        value: current.peakUsage,
        change: calculateChange(current.peakUsage, previous.peakUsage)
      }
    };
  }, [data]);

  return (
    <Grid container spacing={2} columns={12}>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <MetricCard
          title="Avg Charging Time"
          value={stats.chargingTime.value}
          change={stats.chargingTime.change}
          tooltip="Average time users spend charging their vehicles"
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <MetricCard
          title="Avg Target SOC"
          value={stats.targetSoc.value}
          change={stats.targetSoc.change}
          tooltip="Average target State of Charge requested by users"
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <MetricCard
          title="Completion Rate"
          value={stats.completionRate.value}
          change={stats.completionRate.change}
          tooltip="Percentage of charging sessions completed successfully"
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <MetricCard
          title="Peak Usage"
          value={stats.peakUsage.toString()}
          change={stats.peakUsage.change}
          tooltip="Highest number of concurrent charging sessions"
        />
      </Grid>
    </Grid>
  );
}